import { Dialog, DialogContent } from '@/components/ui/dialog'
import { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface FileDialogProps {
  attachments: wowaudit.ApplicationAnswerAttachment[]
  initialIndex: number
  open: boolean
  onOpenChange: (open: boolean) => void
}

const FileDialog = ({
  attachments,
  initialIndex,
  open,
  onOpenChange,
}: FileDialogProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)

  const currentAttachment = attachments[currentIndex]
  const hasPrevious = currentIndex > 0
  const hasNext = currentIndex < attachments.length - 1

  const handlePrevious = () => {
    if (hasPrevious) {
      setCurrentIndex(currentIndex - 1)
    }
  }

  const handleNext = () => {
    if (hasNext) {
      setCurrentIndex(currentIndex + 1)
    }
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        handlePrevious()
      } else if (event.key === 'ArrowRight') {
        handleNext()
      }
    }

    if (open) {
      window.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, currentIndex, hasPrevious, hasNext])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='w-[95vw] h-[95vh] max-w-[95vw] max-h-[95vh] p-0'>
        <div className='flex flex-col w-full h-full'>
          <div className='flex items-center gap-4 px-2 py-1 h-12 border-b shrink-0'>
            {attachments.length > 1 ? (
              <>
                <div className='flex items-center gap-2'>
                  <Button
                    variant='ghost'
                    size='icon'
                    onClick={handlePrevious}
                    disabled={!hasPrevious}
                  >
                    <ChevronLeft className='h-4 w-4' />
                  </Button>
                  <Button
                    variant='ghost'
                    size='icon'
                    onClick={handleNext}
                    disabled={!hasNext}
                  >
                    <ChevronRight className='h-4 w-4' />
                  </Button>
                </div>

                <span className='text-xs text-muted-foreground shrink-0'>
                  <Tooltip>
                    <TooltipTrigger>
                      <span className='text-xs text-muted-foreground shrink-0'>
                        {currentIndex + 1} / {attachments.length}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side='bottom'>
                      {currentAttachment?.originalFilename}
                    </TooltipContent>
                  </Tooltip>
                </span>
              </>
            ) : (
              <span className='text-sm truncate min-w-0 max-w-[calc(95vw-60px)] flex-1 pl-2'>
                {currentAttachment?.originalFilename}
              </span>
            )}
          </div>

          <figure className='h-[calc(95vh-50px)]'>
            <img
              src={currentAttachment?.url}
              alt={currentAttachment?.originalFilename}
              className='object-contain w-full h-full'
            />
          </figure>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default FileDialog
